import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useQuery, useQueryClient } from 'react-query'
import { informalFeedbackRequestsKeys } from './informalFeedbackRequestsKeys'
import { InformalFeedbackRequestsService } from 'api/services/InformalFeedbackRequestsService'
import { useStore } from 'react-redux'
import { InformalFeedbackRequest, QueryOptions } from 'api/clients/InformalFeedbackRequestsClient'
import produce from 'immer'

interface Props extends QueryOptions {
  articleKey: string
  layerId?: string
  staleTime?: number
  enabled?: boolean
}

type CacheData = { informal_feedback_requests: InformalFeedbackRequest[] } | undefined

export const useInformalFeedbackRequests = ({ articleKey, layerId, enabled = true, ...rest }: Props) => {
  const {
    staleTime = 1000 * 60 * 5, // time in milliseconds
    ...options
  } = rest
  const queryClient = useQueryClient()
  const store = useStore()
  return useQuery(
    informalFeedbackRequestsKeys.list(articleKey, layerId, options),
    async () => {
      if (!enabled) return null

      const res = await InformalFeedbackRequestsService.index(store, articleKey, layerId, options)
      if (res.type !== SUCCESS_RESPONSE_TYPE) {
        throw new Error()
      }

      res.data.informal_feedback_requests.forEach((request) => {
        queryClient.setQueriesData<{ informal_feedback_request: InformalFeedbackRequest } | undefined>(
          [informalFeedbackRequestsKeys.prefix, 'detail', request.id],
          (old) => {
            return produce(old, (draft) => {
              if (!draft) {
                return
              }

              draft.informal_feedback_request = request
            })
          },
        )

        if (layerId) {
          queryClient.setQueriesData<{ informal_feedback_request: InformalFeedbackRequest } | undefined>(
            informalFeedbackRequestsKeys.list(articleKey, undefined, options),
            (old) => {
              return produce(old, (draft) => {
                if (!draft) {
                  return
                }

                draft.informal_feedback_request = request
              })
            },
          )
        }
      })

      return res.data
    },
    {
      initialData: () => {
        const existingArticleRequests = queryClient.getQueryData<CacheData>(
          [informalFeedbackRequestsKeys.prefix, articleKey],
          { exact: false },
        )?.informal_feedback_requests

        const matchedRequests = existingArticleRequests?.filter((request) => {
          const matchesStates = options.states ? options.states.includes(request.state) : true
          const matchesLayer = layerId ? request.layer?.id === layerId : true
          const matchesAutomation = options.automated !== undefined ? request.automated === options.automated : true
          return matchesStates && matchesLayer && matchesAutomation
        })

        if (!matchedRequests || matchedRequests.length < 1) return

        return {
          informal_feedback_requests: matchedRequests,
        }
      },
      refetchOnWindowFocus: false,
      staleTime,
    },
  )
}
